import { ViewConfig } from "@lib/views";
import { DATE_COLUMN_SIZE } from "@design-system/ViewTable/DefaultColumnSizes";
import {
    ColumnPin,
    View,
    ViewDensity,
    ViewType,
} from "@common/types/views/views";
import { ResourceType } from "@common/types/auditLog";
import { LogTimeCell } from "../cells/LogTimeCell";
import { HistoryDetailsEntityDisplayCell } from "../cells/HistoryDetailsEntityDisplayCell";
import {
    ScheduleActivityArrowCell,
    ScheduleActivityTextCell,
} from "./ScheduleActivityCells";
import {
    ScheduleActivityRow,
    ScheduleActivityTableKind,
} from "./scheduleActivityRows";

export enum ScheduleActivityDetailsColumn {
    TIME = "time",
    POLICY = "policy",
    GROUP = "group",
    SOURCE = "source",
    ENTITY = "entity",
    PREVIOUS = "previous",
    MODIFIED = "modified",
    ARROW = "arrow",
}

export interface ScheduleActivityDetailsView extends View {
    type: ViewType.HISTORY_UPDATED_TRANSACTION;
    columns: Array<{
        id: ScheduleActivityDetailsColumn;
        options?: { pin?: ColumnPin };
    }>;
}

interface ScheduleActivityTableShape {
    kind: ScheduleActivityTableKind;
    resourceType: ResourceType;
}

export const createDefaultScheduleActivityDetailsView = ({
    kind,
    resourceType,
}: ScheduleActivityTableShape): ScheduleActivityDetailsView => {
    const isSchedule = resourceType === ResourceType.SCHEDULE_APPLICATION;
    const isChange = kind === ScheduleActivityTableKind.CHANGE;
    const columnIds = [
        ScheduleActivityDetailsColumn.TIME,
        ScheduleActivityDetailsColumn.POLICY,
        ScheduleActivityDetailsColumn.GROUP,
        ...(isSchedule ? [ScheduleActivityDetailsColumn.SOURCE] : []),
        ScheduleActivityDetailsColumn.ENTITY,
        ...(isChange
            ? [
                  ScheduleActivityDetailsColumn.PREVIOUS,
                  ScheduleActivityDetailsColumn.MODIFIED,
              ]
            : []),
    ];
    return {
        type: ViewType.HISTORY_UPDATED_TRANSACTION,
        columns: [
            ...columnIds.map((id) => ({ id })),
            {
                id: ScheduleActivityDetailsColumn.ARROW,
                options: { pin: "right" },
            },
        ],
        filters: {},
        options: { density: ViewDensity.MD },
    };
};

export const createScheduleActivityDetailsViewConfig = (
    changedFieldLabel: string | null,
): ViewConfig<ScheduleActivityRow, ScheduleActivityDetailsColumn, never> => {
    const previousLabel = changedFieldLabel
        ? `Previous ${changedFieldLabel}`
        : "Previous";
    const modifiedLabel = changedFieldLabel
        ? `Modified ${changedFieldLabel}`
        : "Modified";

    return {
        columns: {
            [ScheduleActivityDetailsColumn.TIME]: {
                name: "Time",
                header: "",
                accessorKey: "logDate",
                cell: LogTimeCell,
                ...DATE_COLUMN_SIZE,
            },
            [ScheduleActivityDetailsColumn.POLICY]: {
                name: "Policy",
                header: "Policy",
                accessorKey: "policyName",
                cell: ScheduleActivityTextCell,
                minSize: 200,
                maxSize: 360,
            },
            [ScheduleActivityDetailsColumn.GROUP]: {
                name: "Group",
                header: "Group",
                accessorKey: "group",
                cell: ScheduleActivityTextCell,
                minSize: 140,
                maxSize: 220,
            },
            [ScheduleActivityDetailsColumn.SOURCE]: {
                name: "Source",
                header: "Source",
                accessorKey: "sourceLabel",
                cell: ScheduleActivityTextCell,
                minSize: 160,
                maxSize: 260,
            },
            [ScheduleActivityDetailsColumn.ENTITY]: {
                name: "Entity",
                header: "Entity",
                accessorKey: "entityId",
                cell: HistoryDetailsEntityDisplayCell,
                minSize: 140,
                maxSize: 220,
            },
            [ScheduleActivityDetailsColumn.PREVIOUS]: {
                name: previousLabel,
                header: previousLabel,
                accessorKey: "previous",
                cell: ScheduleActivityTextCell,
                minSize: 160,
                maxSize: 300,
            },
            [ScheduleActivityDetailsColumn.MODIFIED]: {
                name: modifiedLabel,
                header: modifiedLabel,
                accessorKey: "modified",
                cell: ScheduleActivityTextCell,
                minSize: 160,
                maxSize: 300,
            },
            [ScheduleActivityDetailsColumn.ARROW]: {
                name: "",
                header: "",
                accessorKey: "link",
                cell: ScheduleActivityArrowCell,
                immutable: true,
                hiddenFromSettings: true,
                minSize: 64,
                maxSize: 64,
            },
        },
    };
};
